import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { ViewAsRoleValue } from 'features/auth/context/view-as-role-context-def'
import { ViewAsRoleContext } from 'features/auth/context/view-as-role-context-def'
import { useProjectStore } from 'features/project/store'
import type { Role } from 'types/hub-role'
import type { ProjectState } from 'types/project'

export interface ViewAsRoleProviderProps {
  readonly children: ReactNode
}

const STORAGE_KEY = 'view-as-previewed-hub-role'

const readPersisted = (): Role | null => {
  try {
    return (sessionStorage.getItem(STORAGE_KEY) as Role | null) ?? null
  } catch {
    return null
  }
}

const persist = (role: Role | null): void => {
  try {
    if (role === null) sessionStorage.removeItem(STORAGE_KEY)
    else sessionStorage.setItem(STORAGE_KEY, role)
  } catch {
    // Best-effort: sessionStorage may be unavailable (private mode, etc.).
  }
}

export const ViewAsRoleProvider = ({
  children,
}: ViewAsRoleProviderProps): JSX.Element => {
  const [previewedHubRole, setPreviewed] = useState<Role | null>(readPersisted)

  // Active hub uuid — clearing the preview when it changes keeps the previewed
  // role from leaking across hubs (and across sign-out, which resets the store).
  const activeHubUuid = useProjectStore(
    (s: ProjectState) => s.details?.proj_uuid ?? '',
  )
  const previousHubUuid = useRef(activeHubUuid)

  const setPreviewedHubRole = useCallback((role: Role): void => {
    setPreviewed(role)
    persist(role)
  }, [])

  const clearPreview = useCallback((): void => {
    setPreviewed(null)
    persist(null)
  }, [])

  useEffect(() => {
    if (previousHubUuid.current !== activeHubUuid) {
      previousHubUuid.current = activeHubUuid
      clearPreview()
    }
  }, [activeHubUuid, clearPreview])

  const value = useMemo<ViewAsRoleValue>(
    () => ({ previewedHubRole, setPreviewedHubRole, clearPreview }),
    [previewedHubRole, setPreviewedHubRole, clearPreview],
  )

  return (
    <ViewAsRoleContext.Provider value={value}>
      {children}
    </ViewAsRoleContext.Provider>
  )
}
